import {UI} from "../../../../../stem-core/ui/UIBase";

import {Messages} from "../../../../Messages.js";
import {CheckboxInput} from "../../../../../stem-core/ui/input/checkbox/CheckboxInput.jsx";
import {registerStyle} from "../../../../../stem-core/ui/style/Theme";
import {styleRule, styleRuleInherit} from "../../../../../stem-core/decorators/Style";
import {authService} from "../../../../../client/connection/services/AuthService.js";
import {userService} from "../../../../../client/connection/services/UserService.js";
import {BlinkInput} from "../../../../ui/Input.jsx";
import {iFrameMerchantService} from "../../../../services/IframeMerchantService.js";
import {iFrameState} from "../../../../services/IFrameState.js";
import {
    apiUserUpdateNewsletterSubscriptions, MerchantAudienceMember,
    NewsletterSubscriptionUpdateType
} from "../../../../../client/state/merchant/MerchantAudienceMemberStore.js";
import {INLINE_NEWSLETTER_URLS} from "../../../PanelConstants.js";
import {Router} from "../../../../../stem-core/ui/Router.jsx";
import {PanelActionButton} from "../../../panel-elements/ActionButton.jsx";
import {isSmallScreen, toArray} from "../../../../UtilsLib.js";
import {FLOW_CHECKPOINT, FLOW_TYPE, GATE_TYPE} from "../../../../../blink-sdk/Constants.js";
import {MessageElement} from "../../../../widget/ui/MessageElement.jsx";
import {MerchantUser} from "../../../../../client/state/merchant/MerchantUserStore.js";
import {wrapInSpinner} from "../../../../../core/ui/LoadingSpinner.jsx";
import {BasePanelPage, BasePanelPageStyle} from "../../../PanelPage.jsx";
import {
    LOGIN_WITH_EMAIL_CODE_URL
} from "../../../../Constants";
import {ApiErrors} from "../../../../../client/connection/ApiErrors";
import {sendFlowCheckpoint} from "../../../flows/BaseFlowHandler.js";
import {Toast} from "../../../../ui/Toast.jsx";
import {Flow} from "../../../flows/Flow.js";
import {VisibilityStatus} from "../../../../../client/state/misc/GenericEnums.js";

// TODO @branch this seems way too complex
export class NewsletterSubscribePanelStyle extends BasePanelPageStyle {
    @styleRule
    container = {
        textAlign: "center",
    }

    @styleRule
    checkbox = {
        marginTop: 12,
        textAlign: "left",
    };

    @styleRuleInherit
    message = {
        marginTop: 16,
        fontSize: 15,
        lineHeight: 22,
        textAlign: () => (isSmallScreen() || iFrameState.gateType === GATE_TYPE.banner) ? "left" : "center",
        ...iFrameState.textStyle,
    };

    @styleRule
    separatorLine = {
        width: "100%",
        height: 0.5,
        background: "#E5E5E5",
        marginTop: 12,
    };

    @styleRule
    button = {
        marginTop: 20,
    };
}

@registerStyle(NewsletterSubscribePanelStyle)
export class NewsletterSubscribePanel extends BasePanelPage {
    fields = null;

    getDefaultOptions(options) {
        return {
            ...super.getDefaultOptions(options),
            title: () => Messages.newsletterTitle(iFrameMerchantService.getMerchant()),
        };
    }

    renderNewsletters() {
        const {styleSheet} = this;
        const newsletters = Flow.newsletter.getNewsletters();

        if (!this.fields) {
            this.fields = {};
            for (const newsletter of newsletters) {
                this.fields[newsletter.id] = true;
            }
        }
        if (newsletters.length === 1) {
            const newsletter = newsletters[0];
            const message = Messages.newsletterDescription(newsletter.name);
            // TODO I really don't like this, we should have better panels
            return message && <div className={styleSheet.message}>{message}</div>;
        }
        return newsletters.map(({name, id}) => <CheckboxInput
            label={name}
            initialValue={this.fields[id]}
            className={styleSheet.checkbox}
            onChange={enabled => {
                this.fields[id] = enabled;
                this.redraw();
            }}/>);
    }

    render() {
        const {styleSheet} = this;
        const newsletters = Flow.newsletter.getNewsletters();
        const merchantName = iFrameMerchantService.getMerchant().getName();
        const merchantUser = MerchantUser.getByUserAndMerchant(userService.getUserId(), iFrameMerchantService.merchantId);

        const [authTitle, unauthTitle] = (newsletters.length === 1) ? [
            Messages.newsletterSubscribeSingleNewsletterAuthenticatedTitle,
            Messages.newsletterSubscribeSingleNewsletterUnauthenticatedTitle
        ] : [
            Messages.newsletterSubscribeMultipleNewsletterAuthenticatedTitle,
            Messages.newsletterSubscribeMultipleNewsletterUnauthenticatedTitle
        ];

        return [
            <div className={styleSheet.leftAlign}>
                {authService.isAuthenticated() ? authTitle : unauthTitle}
            </div>,

            !authService.isAuthenticated() && <BlinkInput
                ref="emailInput"
                inputAttributes={{type: "email"}}
                label={Messages.emailAddress}
                error={this.emailError}
                onFocus={() => this.clearErrors()}
            />,

            this.renderNewsletters(),

            newsletters.length !== 1 && [
                <div className={styleSheet.separatorLine}/>,
                <CheckboxInput
                    className={styleSheet.checkbox}
                    label={Messages.allowPromotionalEmailsFromMerchant(merchantName)}
                    initialValue={!merchantUser || merchantUser.canReceivePromotionalEmails}
                    ref="promotionalEmailsCheckbox"
                />,
            ],

            <PanelActionButton
                label={Messages.subscribeToNewsletter}
                className={styleSheet.button}
                onClick={() => this.subscribeToNewsletter(this.emailInput?.getValue() || userService.getUserEmail(), this.promotionalEmailsCheckbox?.getValue())}
            />,

            authService.isAuthenticated() && <MessageElement
                className={styleSheet.message}
                message={newsletters.length === 1 ?
                    Messages.newsletterEmailDescription(userService.getUserEmail()) :
                    Messages.newslettersEmailDescription(userService.getUserEmail())}
            />,
        ];
    }

    clearErrors() {
        this.emailError = null;
        this.redraw();
    }

    @wrapInSpinner
    async subscribeToNewsletter(emailAddress, canReceivePromotionalEmails) {
        const {merchantId} = iFrameMerchantService;
        const newsletters = Flow.newsletter.getNewsletters();
        const merchantUser = MerchantUser.getByUserAndMerchant(userService.getUserId(), merchantId);
        const updatedCanReceivePromotionalEmails = !merchantUser || canReceivePromotionalEmails !== merchantUser.canReceivePromotionalEmails;

        try {
            const newsletterIds = Object.keys(this.fields).filter(key => this.fields[key]);
            const response = await apiUserUpdateNewsletterSubscriptions({
                emailAddress,
                merchantId,
                updateType: newsletters.length === 1 ? NewsletterSubscriptionUpdateType.SUBSCRIBE : NewsletterSubscriptionUpdateType.REPLACE,
                newsletterIds,
                ...(updatedCanReceivePromotionalEmails && canReceivePromotionalEmails !== null ? {canReceivePromotionalEmails} : {}),
            });

            sendFlowCheckpoint(FLOW_TYPE.newsletter, FLOW_CHECKPOINT.complete, {
                newsletterIds,
                canReceivePromotionalEmails,
            }, {
                audienceMembers: MerchantAudienceMember.loadRaw(response),
            });

            Router.changeURL(INLINE_NEWSLETTER_URLS.success);
        } catch (error) {
            if (error.code === ApiErrors.PERMISSION_DENIED && !authService.isAuthenticated()) {
                // TODO @cleanup reimplement this in a general way?
                await authService.requestLoginCode({
                    email: emailAddress,
                    secure: false,
                });
                Router.changeURL(LOGIN_WITH_EMAIL_CODE_URL);
                // TODO @flow this is dumb, since we might login as someone else. Should be right after this exact code is confirmed
                authService.executeWhenAuthenticated(() => this.subscribeToNewsletter(emailAddress, canReceivePromotionalEmails));
            } else {
                Toast.showError(error);
            }
        }
    }
}
